import { fetchAuthSession, getCurrentUser } from '@aws-amplify/auth';
import config from '../../../../config';

const API = config.API_ADDRESS;

async function getAuthHeaders() {
  try {
    const { tokens } = await fetchAuthSession();
    const user = await getCurrentUser();
    if (tokens?.idToken && user?.username) {
      return {
        Authorization: `Bearer ${tokens.idToken.toString()}`,
        'user-id': user.username,
      };
    }
  } catch {
    // Not authenticated
  }
  return {};
}

async function apiFetch(url, options = {}) {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(url, {
    ...options,
    headers: { ...authHeaders, ...(options.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Request failed: ${res.status}`);
    err.response = { status: res.status, data: body };
    throw err;
  }
  return res.json();
}

export function createTriggerAdapter(componentId, instanceId, { preview = false } = {}) {
  let _responseMode = 'FILL_SUBMIT';
  let _instanceId = instanceId || null;
  let _pinnedVersion = null; // captured from first /meta response, sent on all subsequent calls
  const previewParam = preview ? '?preview=true' : ''; // used in bootstrap() before version pinning

  /** Build query string with pinned version and optional preview flag. */
  function qs() {
    const parts = [];
    if (preview) parts.push('preview=true');
    if (_pinnedVersion != null) parts.push(`workflowVersion=${_pinnedVersion}`);
    return parts.length ? '?' + parts.join('&') : '';
  }

  const adapter = {
    storagePrefix: 'w86_ml_grant_',

    get instanceId() {
      return _instanceId;
    },

    get capabilities() {
      return {
        canSaveDraft: _responseMode === 'FILL_REEDIT',
        canUpload: true,
        hasServerValidation: true,
        hasProgressStatus: false,
        canRefresh: _responseMode === 'FILL_REEDIT',
        showConfirmationOnSubmit: _responseMode === 'FILL_SUBMIT',
      };
    },

    async bootstrap(grantToken) {
      const headers = {};
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const meta = await apiFetch(
        `${API}/component-service/dynamic-form/meta/${componentId}${previewParam}`,
        { headers }
      );

      if (meta?.notPublished) {
        return { notPublished: true };
      }

      // Pin to the version returned by the first load
      if (meta?.workflowVersion != null && _pinnedVersion == null) {
        _pinnedVersion = meta.workflowVersion;
      }

      _responseMode = meta?.responseMode || 'FILL_SUBMIT';

      const mode = String(meta?.accessPolicy?.mode || 'PUBLIC').toUpperCase();
      const hasFields = Array.isArray(meta?.fields) && meta.fields.length > 0;
      return {
        requiresAuth: mode === 'AUTHENTICATED_USERS' || mode === 'AUTHENTICATED_SELECTED_USERS',
        requiresEmailVerification: mode === 'EMAIL_MAGIC_LINK' && !hasFields,
        disabled: mode === 'DISABLED',
        meta,
      };
    },

    async loadForm(grantToken) {
      const headers = {};
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const meta = await apiFetch(
        `${API}/component-service/dynamic-form/meta/${componentId}${qs()}`,
        { headers }
      );

      if (meta?.notPublished) {
        return { notPublished: true, fields: [], title: '', description: '' };
      }

      // Pin to the version returned by the first load
      if (meta?.workflowVersion != null && _pinnedVersion == null) {
        _pinnedVersion = meta.workflowVersion;
      }

      _responseMode = meta?.responseMode || 'FILL_SUBMIT';

      const form = {
        fields: meta.fields || [],
        title: meta.title,
        description: meta.description,
        formSettings: meta.formSettings,
        responseMode: _responseMode,
        isPreview: meta.isPreview || false,
        workflowVersion: meta.workflowVersion || null,
        publishedAt: meta.publishedAt || null,
      };

      if (_instanceId && _responseMode === 'FILL_REEDIT') {
        try {
          const draft = await apiFetch(
            `${API}/component-service/dynamic-form/${componentId}/draft/${_instanceId}${qs()}`,
            { headers }
          );
          if (draft?.hasDraft && draft.answers) {
            form.answers = draft.answers;
          }
        } catch {
          // Instance not found or expired — start fresh
          _instanceId = null;
        }
      }

      return form;
    },

    async saveDraft(answers, grantToken) {
      if (_responseMode !== 'FILL_REEDIT') return null;
      const headers = { 'Content-Type': 'application/json' };
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const result = await apiFetch(
        `${API}/component-service/dynamic-form/${componentId}/draft${qs()}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ answers, instanceId: _instanceId }),
        }
      );
      if (result?.instanceId) {
        _instanceId = result.instanceId;
      }
      return result;
    },

    async validate(answers) {
      return apiFetch(
        `${API}/component-service/dynamic-form/${componentId}/validate${qs()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers, submitMode: false }),
        }
      );
    },

    async submit(answers, grantToken) {
      return apiFetch(
        `${API}/component-service/dynamic-form/${componentId}/submit${qs()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers, verificationGrantToken: grantToken || undefined }),
        }
      );
    },

    async upload(fieldKey, file, grantToken) {
      const formData = new FormData();
      formData.append('file', file);
      const authHeaders = await getAuthHeaders();
      const headers = { ...authHeaders };
      if (grantToken) headers['x-verification-grant-token'] = grantToken;
      const res = await fetch(
        `${API}/component-service/dynamic-form/${componentId}/upload${qs()}`,
        { method: 'POST', headers, body: formData }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.error || 'Upload failed');
        err.response = { status: res.status, data: body };
        throw err;
      }
      return res.json();
    },

    async requestMagicLink(email) {
      await apiFetch(
        `${API}/component-service/dynamic-form/${componentId}/magic-link/request${qs()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      );
    },

    async verifyMagicLink(verificationToken) {
      return apiFetch(
        `${API}/component-service/dynamic-form/magic-link/verify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ verificationToken }),
        }
      );
    },

    deriveReadOnly(form) {
      if (!form) return false;
      const rm = form?.responseMode || _responseMode;
      return rm === 'VIEW_ONLY';
    },

    deriveCanSubmit(form) {
      if (!form) return true;
      const rm = form?.responseMode || _responseMode;
      return rm !== 'VIEW_ONLY';
    },

    deriveCanEdit(form) {
      if (!form) return true;
      const rm = form?.responseMode || _responseMode;
      return rm !== 'VIEW_ONLY';
    },
  };

  return adapter;
}
